import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { dispatchService, HandcuffReadableReasons, HandcuffReason, HandcuffReasonReadableTypes } from '../../../services/central-api/dispatch';
import ActingBodiesDropdown from '../OccurrenceTypes/ActingBodiesDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import { useAuth } from '../../../context/Auth';

export default function HandcuffReasonsModal({ onClose, handcuffReason, isModalVisible }: { onClose: () => void; handcuffReason?: HandcuffReason; isModalVisible: boolean; }) {
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState(handcuffReason?.name || '');
    const [type, setType] = useState<keyof HandcuffReasonReadableTypes | null>(handcuffReason?.type ?? HandcuffReadableReasons.NONE);
    const [actingBodyId, setActingBodyId] = useState<number>(handcuffReason?.actingBody.id ?? 0);

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    function clearValues() {
        setName('');
        setType(null);
        setActingBodyId(0);
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (handcuffReason) {
            setName(handcuffReason.name);
            setType(handcuffReason.type);
            setActingBodyId(handcuffReason.actingBody.id);
        } else if (!(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_handcuff_reasons) && authenticatedUser.actingBodyUnit.actingBodyId) {
            setActingBodyId(authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    async function formSubmit() {

        try {
            if (handcuffReason?.id) {
                await dispatchService.updateHandcuffReason({
                    id: handcuffReason.id,
                    name,
                    type,
                });
                onClose();
                return;
            }

            await dispatchService.createHandcuffReason({
                name: name,
                actingBodyId,
                type,
            });
            onClose();
        } catch (err) {
            console.error(err);
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                        {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_handcuff_reasons) &&
                            <ActingBodiesDropdown includeAllOption={false} value={actingBodyId} setValue={setActingBodyId} zIndex={3} disabled={!!handcuffReason?.id} />
                        }

                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            value={name}
                            onChangeText={setName}
                            invalid={isNameInvalid}
                        />

                        <FilterDropdown
                            label={translate('type')}
                            items={Object.entries(HandcuffReadableReasons).map(([key, value]) => ({
                                label: translate(key),
                                value: value ?? undefined
                            }))}
                            multiple={false}
                            setValue={setType}
                            value={type ?? ''}
                            zIndex={2}
                            labelFontSize={14}
                        />

                        <FormActions
                            onSubmit={formSubmit}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            buttonStyle={{ width: '100%' }}
                            viewStyle={{ width: '100%' }}
                            disabled={!isFormValid()} />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 10,
    },
}));
